import React, { useEffect, useMemo, useState } from 'react';
import { View } from "@/styles/reactnative";
import BidsSection from "./BidsSection";
import AsksSection from "./AsksSection";
import OrderBookHeader from "./OrderBookHeader";
import OrderBookCurrentPrice from "./OrderBookCurrentPrice";
import useTradeStore from "../../../../hooks/trade/tradeStore";
import throttle from 'lodash/throttle';
import { useBinanceWebSocketStore } from '@app/hooks/binanceWebsocketStore.ts';
import { useCiTradeWebSocketStore } from '@app/hooks/ciTradeWebsocketStore.ts';

const OrderBook = ({ filterTab, height = 500, selected, setOrderBookValue }: any) => {
  const { data: tradeStore } = useTradeStore();
  const depthAsks = useCiTradeWebSocketStore((s) => s.depthAsks);
  const depthBids = useCiTradeWebSocketStore((s) => s.depthBids);
  const tradePrice = useCiTradeWebSocketStore((s) => s.tradePrice);

  const [throttledLong, setThrottledLong] = useState<any[]>([]);
  const [throttledShort, setThrottledShort] = useState<any[]>([]);
  const [max, setMax] = useState<number>(0);

  const formattedOrderBook = useMemo(() => {
    if (!depthAsks || !depthBids || !tradeStore?.pairs) return null;

    const rawSymbol = tradeStore?.pairs.replace('/', '').toLowerCase();
    const isPepe = rawSymbol === 'pepeusdt';

    const formatSide = (side: any[]) => {
      let runningSum = 0;
      let localMax = 0;

      const formatted = side.map(([priceStr, quantityStr]: [string, string]) => {
        let price = parseFloat(priceStr);
        let quantity = parseFloat(quantityStr);

        if (isPepe) {
          price *= 1000;
          quantity /= 1e7;
        }

        const sum = price * quantity;
        runningSum += sum;
        localMax = Math.max(localMax, runningSum);

        return {
          price,
          amount: quantity,
          sumAmount: runningSum,
        };
      });

      return { formatted, max: localMax };
    };

    const bids = formatSide(depthBids);
    const asks = formatSide([...depthAsks].reverse());

    return {
      long: bids.formatted,
      short: asks.formatted,
      max: Math.max(bids.max, asks.max),
    };
  }, [depthAsks, depthBids, tradeStore?.pairs]);

  const throttledUpdate = useMemo(() =>
    throttle((newData: any) => {
      setThrottledLong(newData?.long);
      setThrottledShort(newData?.short);
      setMax(newData?.max);
    }, 1000), []);

  useEffect(() => {
    if (formattedOrderBook) throttledUpdate(formattedOrderBook);
  }, [formattedOrderBook]);

  return (
    <View style={{ ...styleSize, justifyContent: "flex-start" }}>
      <OrderBookHeader />
      <div style={{ width: "100%", height: height, overflowY: "hidden" }}>
        {filterTab === "one" ? (
          <View style={{ ...styleSize }}>
            <AsksSection asks={throttledShort} max={max} selected={selected} setOrderBookValue={setOrderBookValue} />
            <OrderBookCurrentPrice tradeDataP={tradePrice || "0"} />
            <BidsSection bids={throttledLong} max={max} selected={selected} setOrderBookValue={setOrderBookValue} />
          </View>
        ) : filterTab === "two" ? (
          <View style={{ ...styleSize }}>
            <BidsSection bids={throttledLong} max={max} selected={selected} setOrderBookValue={setOrderBookValue} />
          </View>
        ) : (
          <View style={{ ...styleSize }}>
            <AsksSection asks={throttledShort} max={max} selected={selected} setOrderBookValue={setOrderBookValue} />
          </View>
        )}
      </div>
    </View>
  );
};


const styleSize = {
  width: "100%",
  height: "100%",
};

export default React.memo(OrderBook);
